import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';

interface ResponsiveWizardShellProps {
  /**
   * Whether the wizard is open. Drives the Radix Dialog `open` prop on both
   * mobile and desktop variants.
   */
  open: boolean;
  /**
   * Called when Radix wants to change the open state (overlay click, ESC,
   * X click, etc.). The parent owns the close logic and decides whether to
   * honor it.
   */
  onOpenChange: (open: boolean) => void;
  /**
   * Required for screen-reader announcement when the wizard opens. Wrapped
   * in `sr-only` -- the wizard's existing visible header is preserved for
   * sighted users. Radix Dialog logs a warning if Title is missing.
   */
  title: string;
  /**
   * Optional. Same SR-only treatment as `title`. Use when the wizard's
   * accessible context isn't obvious from the title alone.
   */
  description?: string;
  /**
   * Override the default content className. Use sparingly -- the defaults
   * (mobile: bottom sheet, full dvh; desktop: centered, max-w-3xl, 90vh)
   * are tuned to the 3 production wizards.
   */
  className?: string;
  /**
   * Optional className applied to the full-viewport overlay (the backdrop
   * behind the content). Use this to customize the backdrop, NOT
   * `className` -- `className` only affects the content box (which is
   * `max-w-3xl` on desktop and full-width on mobile). For example, the
   * campaign wizard uses `bg-background/95 backdrop-blur-sm` to preserve
   * its pre-Phase-6b backdrop visual across the whole viewport.
   *
   * Defaults to Radix's `bg-black/80` from the shared overlay primitives.
   */
  overlayClassName?: string;
  /**
   * Wizard body. Should include the wizard's own visible header (progress,
   * back arrow, X) -- the shell does NOT render any chrome of its own
   * other than the SR-only title/description.
   */
  children: ReactNode;
}

/**
 * `ResponsiveWizardShell` -- the canonical wrapper for full-screen wizard
 * modals introduced in Phase 6b (REK-1546). Replaces the legacy
 * `<div className="fixed inset-0 z-50 ...">` pattern with shadcn's Radix
 * Dialog primitives, picking between bottom-sheet on mobile (<md) and a
 * centered dialog on desktop (>=md).
 *
 * Mobile (<md): `<Sheet side="bottom">` -- full-height, slides up, rounded
 *   top corners. Click-outside + ESC dismiss are wired automatically.
 * Desktop (>=md): `<Dialog>` -- centered, max-w-3xl, h-90vh. Same dismiss
 *   contract.
 *
 * The wizard's own header close button stays in place (typically wired to
 * `onClose()` which the parent translates into `onOpenChange(false)`).
 * `hideCloseButton` is passed to both DialogContent + SheetContent to
 * prevent the duplicate built-in X.
 *
 * Why a JS-based switch via `useIsMobile()` instead of CSS-only responsive
 * Tailwind: Sheet's slide-in-from-bottom and Dialog's fade-and-zoom use
 * different Radix data-state animations. Layering them via media-query
 * className overrides would require copying ~5 animation classes per
 * direction and would still produce both portal roots simultaneously
 * (wasting DOM + creating focus-trap ambiguity).
 *
 * @see docs/context/FEATURE_SPECS/shadcn-design-system-migration.md
 * @see https://linear.app/reklaimyou/issue/REK-1546
 */
export function ResponsiveWizardShell({
  open,
  onOpenChange,
  title,
  description,
  className,
  overlayClassName,
  children,
}: ResponsiveWizardShellProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          hideCloseButton
          overlayClassName={overlayClassName}
          className={cn(
            'h-dvh max-h-dvh w-full p-0 gap-0 flex flex-col border-t-0 rounded-t-2xl',
            className,
          )}
        >
          <SheetTitle className="sr-only">{title}</SheetTitle>
          {description && (
            <SheetDescription className="sr-only">{description}</SheetDescription>
          )}
          {children}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton
        overlayClassName={overlayClassName}
        className={cn(
          // `sm:rounded-2xl` is required to defeat `DialogContent`'s base
          // `sm:rounded-sm`. `tailwind-merge` keeps base + responsive-prefixed
          // border-radius classes as separate groups, so the base `sm:rounded-sm`
          // wins via CSS source order at >=sm (and the Dialog only renders at
          // >=md anyway). Without the sm: variant the wizard's desktop corners
          // collapse to 2px instead of 16px. (Bugbot R1 trap on PR #302.)
          'max-w-3xl h-[90vh] max-h-[90vh] w-full p-0 gap-0 flex flex-col rounded-2xl sm:rounded-2xl',
          className,
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {description && (
          <DialogDescription className="sr-only">{description}</DialogDescription>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
}
